// src/screens/FoodTypes.jsx
import 'bootstrap-icons/font/bootstrap-icons.css';
import { useEffect, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { useLogs } from '../context/LogContext';
import '../css/FoodTypes.css';
import useIdleLogout from "../hooks/useIdleLogout";

/* =========================================================
   API bases from env
   ========================================================= */
const API_BASE = (process.env.REACT_APP_FOODTYPES_API_URL || '').trim();
const CATEGORY_API_BASE = (process.env.REACT_APP_FOOD_CATEGORIES_API_URL || '').trim();

/* =========================================================
   Single source of truth for ORIGIN and upload dirs
   ========================================================= */
const ORIGIN = API_BASE ? new URL(API_BASE).origin : (typeof window !== 'undefined' ? window.location.origin : '');
const BT_DIR = '/merchant/uploads/business-types/';
const CAT_DIR = '/merchant/uploads/food-category/';
const ABS_RE = /^(https?:|data:|blob:)/i;
const FILE_RE = /^[^/]+\.(png|jpe?g|webp|gif|svg)$/i;

function normalizePath(raw, dir) {
  const r = String(raw || '').trim();
  if (!r) return '';
  if (ABS_RE.test(r)) return r;

  let p = r
    .replace(/\\\\/g, '/')
    .replace(/^\/+/, '/')
    .replace(/^(public|storage)\//i, '/')
    .replace(/^merchant\/+/, '/merchant/');

  if (/^\/merchant\/uploads\//i.test(p)) return `${ORIGIN}${p}`;
  if (/^\/uploads\//i.test(p)) return `${ORIGIN}/merchant${p}`;
  if (/^uploads\//i.test(p)) return `${ORIGIN}/merchant/${p}`;

  const plain = p.replace(/^\//, '');
  if (FILE_RE.test(plain)) return `${ORIGIN}${dir}${plain}`;
  if (!p.startsWith('/')) p = '/' + p;
  return `${ORIGIN}${p}`;
}

const buildBusinessImgURL = (raw) => normalizePath(raw, BT_DIR);
const buildCategoryImgURL = (raw) => normalizePath(raw, CAT_DIR);

const PLACEHOLDER_DATA_URI =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" fill="none"><rect x="1" y="1" width="62" height="62" rx="12" fill="#f2f2f2" stroke="#e5e5e5"/></svg>`);

function ImageCell({ src, alt = '', size = 64, radius = 10 }) {
  const [ok, setOk] = useState(Boolean(src));
  useEffect(() => { setOk(Boolean(src)); }, [src]);
  return (
    <div className="thumb-wrap" style={{ width: size, height: size, position: 'relative', borderRadius: radius, overflow: 'hidden' }}>
      <img
        src={ok ? src : PLACEHOLDER_DATA_URI}
        alt={alt}
        width={size}
        height={size}
        className="thumb"
        style={{ objectFit: 'cover', width: '100%', height: '100%', display: 'block' }}
        loading="lazy"
        decoding="async"
        draggable={false}
        crossOrigin="anonymous"
        referrerPolicy="no-referrer"
        onError={() => setOk(false)}
      />
    </div>
  );
}

function pickBusinessTypeImg(it) {
  return (
    it.image ??
    it.business_type_image ?? it.businessTypeImage ??
    it.type_image ?? it.typeImage ?? it.food_type_image ?? it.foodTypeImage ??
    it.thumbnail ?? it.thumb ??
    it.image_url ?? it.imageUrl ?? it.imageURL ??
    it.image_path ?? it.imagePath ??
    it.logo_url ?? it.logoUrl ??
    it.photo_url ?? it.photoUrl ??
    it.picture ?? it.icon ?? it.logo ??
    it.file_url ?? it.url ?? it.file_name ??
    it.path ?? ''
  );
}
function pickCategoryImg(it) {
  return (
    it.category_image ?? it.categoryImage ??
    it.image_url ?? it.imageUrl ?? it.imageURL ??
    it.image_path ?? it.imagePath ??
    it.logo_url ?? it.logoUrl ??
    it.photo_url ?? it.photoUrl ??
    it.picture ?? it.icon ?? it.logo ??
    it.file_url ?? it.url ??
    it.image ?? it.path ?? ''
  );
}

function normalizeTypes(raw) {
  const list =
    Array.isArray(raw) ? raw :
      raw?.data ?? raw?.items ?? raw?.results ??
      raw?.business_types ?? raw?.businessTypes ?? [];

  return list
    .map(it => ({
      ...it,
      id: it.id || it._id || it.business_type_id || it.food_type_id || it.type_id,
      name: it.name ?? it.type_name ?? '',
      description: it.description ?? it.note ?? '',
      types: it.types ?? it.category ?? '',
      imgUrl: buildBusinessImgURL(pickBusinessTypeImg(it)),
    }))
    .filter(it => String(it.types || '').toLowerCase() === 'food');
}

function normalizeCategories(raw) {
  const list =
    Array.isArray(raw) ? raw :
      raw?.data ?? raw?.items ?? raw?.results ??
      raw?.categories ?? raw?.foodCategories ?? [];

  return list.map(it => ({
    ...it,
    id: it.id || it._id || it.category_id || it.food_category_id,
    name: it.name ?? it.category_name ?? '',
    description: it.description ?? it.note ?? '',
    business_type: it.business_type ?? it.business_type_name ?? '',
    imgUrl: buildCategoryImgURL(pickCategoryImg(it)),
  }));
}

function getAdminMeta() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return { user_id: u?.user_id ?? u?.id ?? 1, admin_name: u?.user_name ?? u?.full_name ?? u?.name ?? 'Admin' };
  } catch { return { user_id: 1, admin_name: 'Admin' }; }
}

export default function FoodTypes() {
  useIdleLogout();
  const { logActivity } = useLogs();

  const newTypeImageRef = useRef(null);
  const newCategoryImageRef = useRef(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState(null);

  const [categoryName, setCategoryName] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [categoryImageFile, setCategoryImageFile] = useState(null);
  const [selectedBusinessType, setSelectedBusinessType] = useState(''); // keeps API semantics

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [fadeOut, setFadeOut] = useState(false);

  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [editRowData, setEditRowData] = useState({});
  const [originalRowData, setOriginalRowData] = useState({});

  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editCategoryData, setEditCategoryData] = useState({});
  const [originalCategoryData, setOriginalCategoryData] = useState({});

  const fetchAll = async (signal) => {
    if (!API_BASE) { setRows([]); return; }
    try {
      const res = await fetch(`${API_BASE}`, { method: 'GET', signal });
      if (!res.ok) throw new Error('Failed to fetch cuisine types');
      const data = await res.json();
      setRows(normalizeTypes(data));
    } catch (e) {
      if (e.name !== 'AbortError') console.error(e);
      setRows([]);
    }
  };

  const fetchAllCategories = async (signal) => {
    if (!CATEGORY_API_BASE) { setCategories([]); return; }
    try {
      const res = await fetch(`${CATEGORY_API_BASE}`, { method: 'GET', signal });
      if (!res.ok) throw new Error('Failed to fetch food categories');
      const data = await res.json();
      setCategories(normalizeCategories(data));
    } catch (e) {
      if (e.name !== 'AbortError') console.error(e);
      setCategories([]);
    }
  };

  useEffect(() => {
    const ctl1 = new AbortController();
    const ctl2 = new AbortController();
    fetchAll(ctl1.signal);
    fetchAllCategories(ctl2.signal);
    return () => { ctl1.abort(); ctl2.abort(); };
  }, []);

  useEffect(() => {
    if (message) {
      setFadeOut(false);
      const t1 = setTimeout(() => setFadeOut(true), 1500);
      const t2 = setTimeout(() => setMessage(null), 2000);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [message]);

  const fileToDataURL = (file) => new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });

  /* ---------- Cuisine Types handlers ---------- */
  const onNewImageChange = async (e) => {
    const f = e.target.files?.[0];
    setImageFile(f || null);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const { user_id, admin_name } = getAdminMeta();
    if (rows.find(r => (r.name || '').trim().toLowerCase() === name.trim().toLowerCase())) {
      Swal.fire('Duplicate Name', 'That cuisine type already exists.', 'error');
      return;
    }

    try {
      setLoading(true); setMessage(null);

      let res;
      if (imageFile) {
        const fd = new FormData();
        fd.append('name', name.trim());
        fd.append('description', (description || '').trim());
        fd.append('types', 'food');
        fd.append('user_id', String(user_id));
        fd.append('admin_name', admin_name);
        fd.append('image', imageFile);
        res = await fetch(API_BASE, { method: 'POST', body: fd });
      } else {
        const payload = { name: name.trim(), description: (description || '').trim(), types: 'food', user_id, admin_name };
        res = await fetch(API_BASE, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      }

      if (!res.ok) throw new Error(await res.text());
      await res.json();

      logActivity(`Added cuisine type "${name.trim()}"`);
      setMessage({ type: 'success', text: 'Cuisine type added successfully!' });
      setName(''); setDescription('');
      setImageFile(null);
      if (newTypeImageRef.current) newTypeImageRef.current.value = '';
      fetchAll();
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Something went wrong.' });
    } finally { setLoading(false); }
  };

  /* ---------- Food Categories handlers ---------- */
  const onNewCategoryImageChange = async (e) => {
    const f = e.target.files?.[0];
    setCategoryImageFile(f || null);
  };

  const handleCategorySubmit = async e => {
    e.preventDefault();
    const { user_id, admin_name } = getAdminMeta();

    if (!selectedBusinessType) {
      Swal.fire('Required', 'Please select a cuisine type', 'error');
      return;
    }
    if (categories.find(c => (c.name || '').trim().toLowerCase() === categoryName.trim().toLowerCase())) {
      Swal.fire('Duplicate Name', 'That food category already exists.', 'error');
      return;
    }

    const businessTypeName = rows.find(r => String(r.id) === String(selectedBusinessType))?.name || '';

    try {
      setLoading(true); setMessage(null);

      let res;
      if (categoryImageFile) {
        const fd = new FormData();
        fd.append('category_name', categoryName.trim());
        fd.append('description', (categoryDescription || '').trim());
        // API expects the text name under the old key `business_type`
        fd.append('business_type', businessTypeName);
        fd.append('user_id', String(user_id));
        fd.append('admin_name', admin_name);
        fd.append('image', categoryImageFile);
        res = await fetch(CATEGORY_API_BASE, { method: 'POST', body: fd });
      } else {
        const payload = {
          category_name: categoryName.trim(),
          description: (categoryDescription || '').trim(),
          business_type: businessTypeName,
          user_id, admin_name,
        };
        res = await fetch(CATEGORY_API_BASE, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
        });
      }

      if (!res.ok) throw new Error(await res.text());
      await res.json();

      logActivity(`Added food category "${categoryName.trim()}" under cuisine type "${businessTypeName}"`);
      setMessage({ type: 'success', text: 'Food category added successfully!' });
      setCategoryName(''); setCategoryDescription('');
      setCategoryImageFile(null);
      setSelectedBusinessType('');
      if (newCategoryImageRef.current) newCategoryImageRef.current.value = '';
      fetchAllCategories();
    } catch (err) {
      console.error(err);
      setMessage({ type: 'error', text: 'Something went wrong.' });
    } finally { setLoading(false); }
  };

  /* ---------- row edit handlers ---------- */
  const startRowEdit = row => {
    setEditingId(row.id);
    const initial = {
      name: String(row.name ?? ''),
      description: String(row.description ?? ''),
      preview: row.imgUrl || '',
    };
    setEditRowData(initial);
    setOriginalRowData(initial);
  };

  const startCategoryEdit = category => {
    setEditingCategoryId(category.id);
    const resolvedBT = rows.find(r => (r.name || '').toLowerCase() === String(category.business_type || '').toLowerCase());
    const business_type_id = resolvedBT?.id ? String(resolvedBT.id) : '';
    const initial = {
      name: String(category.name ?? ''),
      description: String(category.description ?? ''),
      business_type_id,
      preview: category.imgUrl || '',
    };
    setEditCategoryData(initial);
    setOriginalCategoryData(initial);
  };

  const fileToDataURLForEdit = async (file, setState, base) => {
    if (!file) { setState(prev => ({ ...prev, imgFile: null, preview: base || '' })); return; }
    const preview = await fileToDataURL(file);
    setState(prev => ({ ...prev, imgFile: file, preview }));
  };

  const onEditImageChange = async (e) => fileToDataURLForEdit(e.target.files?.[0], setEditRowData, originalRowData.preview);
  const onEditCategoryImageChange = async (e) => fileToDataURLForEdit(e.target.files?.[0], setEditCategoryData, originalCategoryData.preview);

  const saveRowEdit = async id => {
    const { user_id, admin_name } = getAdminMeta();
    const parsed = {
      name: (editRowData.name ?? '').trim(),
      description: (editRowData.description ?? '').trim(),
      types: 'food',
      user_id,
      admin_name,
    };

    if (rows.find(r => r.id !== id && (r.name || '').trim().toLowerCase() === parsed.name.toLowerCase())) {
      Swal.fire('Duplicate', 'That cuisine type already exists.', 'error');
      return;
    }

    try {
      setLoading(true);
      let res;
      if (editRowData.imgFile) {
        const fd = new FormData();
        Object.entries(parsed).forEach(([k, v]) => fd.append(k, String(v)));
        fd.append('image', editRowData.imgFile);
        res = await fetch(`${API_BASE}/${id}`, { method: 'PUT', body: fd });
      } else {
        res = await fetch(`${API_BASE}/${id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(parsed),
        });
      }
      if (!res.ok) throw new Error(await res.text());
      await res.json();

      Swal.fire('Updated!', 'Cuisine type updated successfully!', 'success');
      setEditingId(null); setEditRowData({}); setOriginalRowData({}); fetchAll();
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Could not update cuisine type.', 'error');
    } finally { setLoading(false); }
  };

  const saveCategoryEdit = async id => {
    const { user_id, admin_name } = getAdminMeta();
    const parsed = {
      name: (editCategoryData.name ?? '').trim(),
      description: (editCategoryData.description ?? '').trim(),
      business_type_id: (editCategoryData.business_type_id ?? '').trim(),
      user_id,
      admin_name,
    };

    if (!parsed.business_type_id) {
      Swal.fire('Required', 'Please select a cuisine type', 'error');
      return;
    }
    if (categories.find(c => c.id !== id && (c.name || '').trim().toLowerCase() === parsed.name.toLowerCase())) {
      Swal.fire('Duplicate', 'That food category already exists.', 'error');
      return;
    }

    const businessTypeName = rows.find(r => String(r.id) === String(parsed.business_type_id))?.name || '';

    try {
      setLoading(true);
      let res;
      if (editCategoryData.imgFile) {
        const fd = new FormData();
        fd.append('category_name', parsed.name);
        fd.append('description', parsed.description);
        fd.append('business_type', businessTypeName); // keep API field
        fd.append('user_id', String(user_id));
        fd.append('admin_name', admin_name);
        fd.append('image', editCategoryData.imgFile);
        res = await fetch(`${CATEGORY_API_BASE}/${id}`, { method: 'PUT', body: fd });
      } else {
        const payload = { category_name: parsed.name, description: parsed.description, business_type: businessTypeName, user_id, admin_name };
        res = await fetch(`${CATEGORY_API_BASE}/${id}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
      }
      if (!res.ok) throw new Error(await res.text());
      await res.json();

      Swal.fire('Updated!', 'Food category updated successfully!', 'success');
      setEditingCategoryId(null); setEditCategoryData({}); setOriginalCategoryData({}); fetchAllCategories();
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Could not update food category.', 'error');
    } finally { setLoading(false); }
  };

  const cancelRowEdit = () => { setEditRowData(originalRowData); setEditingId(null); setOriginalRowData({}); };
  const cancelCategoryEdit = () => { setEditCategoryData(originalCategoryData); setEditingCategoryId(null); setOriginalCategoryData({}); };

  const handleDelete = async id => {
    const { user_id, admin_name } = getAdminMeta();
    const nameToDelete = rows.find(r => r.id === id)?.name;

    const { isConfirmed } = await Swal.fire({
      title: 'Are you sure?', text: 'This cannot be undone.', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Yes, delete',
    });
    if (!isConfirmed) return;

    try {
      const res = await fetch(`${API_BASE}/${id}`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id, admin_name }),
      });
      if (!res.ok) throw new Error(await res.text());
      await res.json();

      Swal.fire('Deleted!', `Cuisine type "${nameToDelete || ''}" removed.`, 'success');
      fetchAll();
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Could not delete.', 'error');
    }
  };

  const handleCategoryDelete = async id => {
    const { user_id, admin_name } = getAdminMeta();
    const nameToDelete = categories.find(c => c.id === id)?.name;

    const { isConfirmed } = await Swal.fire({
      title: 'Are you sure?', text: 'This cannot be undone.', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Yes, delete',
    });
    if (!isConfirmed) return;

    try {
      const res = await fetch(`${CATEGORY_API_BASE}/${id}`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id, admin_name }),
      });
      if (!res.ok) throw new Error(await res.text());
      await res.json();

      Swal.fire('Deleted!', `Food category "${nameToDelete || ''}" removed.`, 'success');
      fetchAllCategories();
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Could not delete.', 'error');
    }
  };

  return (
    <>
      {/* Add Cuisine Type */}
      <div className="card mb-4">
        <div className="card-body">
          {message && <div className={`alert alert-${message.type} ${fadeOut ? 'fade-out' : ''}`}>{message.text}</div>}

          <form onSubmit={handleSubmit} className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Cuisine Type Name:</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter cuisine type"
                value={name}
                onChange={e => setName(e.target.value)}
                required
              />
            </div>

            <div className="col-md-5">
              <label className="form-label">Description:</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter short description"
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">Image:</label>
              <input
                ref={newTypeImageRef}
                type="file"
                accept="image/*"
                className="form-control"
                onChange={onNewImageChange}
              />
            </div>

            <div className="col-12">
              <button type="submit" className="add-type-btn" disabled={loading}>
                {loading ? 'Saving…' : 'Add Cuisine'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Cuisine Types Table */}
      <div className="card mb-4">
        <div className="card-header"><h5 className="mb-0">All Cuisine Types</h5></div>
        <div className="card-body table-responsive food-types-table-card">
          {rows.length === 0 ? (
            <p className="text-muted">No cuisine types added yet.</p>
          ) : (
            <table className="table table-bordered align-middle">
              <colgroup>
                <col style={{ width: '80px' }} />
                <col style={{ width: '90px' }} />
                <col />
                <col />
                <col style={{ width: '160px' }} />
              </colgroup>
              <thead className="table-light">
                <tr>
                  <th>SI.No</th>
                  <th>Image</th>
                  <th>Cuisine Type</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => (
                  <tr key={row.id}>
                    <td>{idx + 1}</td>
                    <td className="text-center">
                      {editingId === row.id ? (
                        <div className="image-picker d-flex flex-column align-items-center gap-1">
                          <ImageCell src={editRowData.preview} alt="" />
                          <label className="ghost-btn mb-0">
                            Change
                            <input type="file" accept="image/*" hidden onChange={onEditImageChange} />
                          </label>
                        </div>
                      ) : <ImageCell src={row.imgUrl} alt={row.name || ''} />}
                    </td>
                    <td>
                      {editingId === row.id ? (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={editRowData.name}
                          onChange={e => setEditRowData(prev => ({ ...prev, name: e.target.value }))}
                        />
                      ) : row.name}
                    </td>
                    <td>
                      {editingId === row.id ? (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={editRowData.description}
                          onChange={e => setEditRowData(prev => ({ ...prev, description: e.target.value }))}
                        />
                      ) : (row.description || <span className="text-muted">—</span>)}
                    </td>
                    <td className="actions-cell">
                      {editingId === row.id ? (
                        <>
                          <button className="btn-confirm me-2" title="Save" onClick={() => saveRowEdit(row.id)}>
                            <i className="bi bi-check-lg" />
                          </button>
                          <button className="btn-cancel" title="Cancel" onClick={cancelRowEdit}>
                            <i className="bi bi-x-lg" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="btn-view me-2" title="Edit" onClick={() => startRowEdit(row)}>
                            <i className="bi bi-pencil" />
                          </button>
                          <button className="btn-delete" title="Delete" onClick={() => handleDelete(row.id)}>
                            <i className="bi bi-trash" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <h2 className="mb-3">Cuisine → Food Category</h2>

      {/* Add Food Category */}
      <div className="card mb-4">
        <div className="card-body">
          <form onSubmit={handleCategorySubmit} className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Cuisine Type:</label>
              <select
                className="form-select"
                value={selectedBusinessType}
                onChange={e => setSelectedBusinessType(e.target.value)}
                required
              >
                <option value="">Select Cuisine Type</option>
                {rows.map(row => (<option key={row.id} value={row.id}>{row.name}</option>))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Food Category Name:</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter food category name"
                value={categoryName}
                onChange={e => setCategoryName(e.target.value)}
                required
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Description:</label>
              <input
                type="text"
                className="form-control"
                placeholder="Enter short description"
                value={categoryDescription}
                onChange={e => setCategoryDescription(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Image:</label>
              <input
                ref={newCategoryImageRef}
                type="file"
                accept="image/*"
                className="form-control"
                onChange={onNewCategoryImageChange}
              />
            </div>
            <div className="col-12">
              <button type="submit" className="add-type-btn" disabled={loading}>
                {loading ? 'Saving…' : 'Add Food Category'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Food Categories Table */}
      <div className="card mb-4">
        <div className="card-header"><h5 className="mb-0">All Food Categories</h5></div>
        <div className="card-body table-responsive food-types-table-card">
          {categories.length === 0 ? (
            <p className="text-muted">No food categories added yet.</p>
          ) : (
            <table className="table table-bordered align-middle">
              <colgroup>
                <col style={{ width: '80px' }} />
                <col style={{ width: '90px' }} />
                <col style={{ width: '220px' }} />
                <col />
                <col />
                <col style={{ width: '160px' }} />
              </colgroup>
              <thead className="table-light">
                <tr>
                  <th>SI.No</th>
                  <th>Image</th>
                  <th>Cuisine Type</th>
                  <th>Food Category</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((category, idx) => (
                  <tr key={category.id}>
                    <td>{idx + 1}</td>
                    <td className="text-center">
                      {editingCategoryId === category.id ? (
                        <div className="image-picker d-flex flex-column align-items-center gap-1">
                          <ImageCell src={editCategoryData.preview} alt="" />
                          <label className="ghost-btn mb-0">
                            Change
                            <input type="file" accept="image/*" hidden onChange={onEditCategoryImageChange} />
                          </label>
                        </div>
                      ) : <ImageCell src={category.imgUrl} alt={category.name || ''} />}
                    </td>
                    <td>
                      {editingCategoryId === category.id ? (
                        <select
                          className="form-select form-select-sm"
                          value={editCategoryData.business_type_id || ''}
                          onChange={e => setEditCategoryData(prev => ({ ...prev, business_type_id: e.target.value }))}
                          required
                        >
                          <option value="">Select Cuisine Type</option>
                          {rows.map(bt => (<option key={bt.id} value={bt.id}>{bt.name}</option>))}
                        </select>
                      ) : (category.business_type || <span className="text-muted">—</span>)}
                    </td>
                    <td>
                      {editingCategoryId === category.id ? (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={editCategoryData.name}
                          onChange={e => setEditCategoryData(prev => ({ ...prev, name: e.target.value }))}
                        />
                      ) : (category.name)}
                    </td>
                    <td>
                      {editingCategoryId === category.id ? (
                        <input
                          type="text"
                          className="form-control form-control-sm"
                 
                          value={editCategoryData.description}
                          onChange={e => setEditCategoryData(prev => ({ ...prev, description: e.target.value }))}
                        />
                      ) : (category.description || <span className="text-muted">—</span>)}
                    </td>
                    <td className="actions-cell">
                      {editingCategoryId === category.id ? (
                        <>
                          <button className="btn-confirm me-2" title="Save" onClick={() => saveCategoryEdit(category.id)}>
                            <i className="bi bi-check-lg" />
                          </button>
                          <button className="btn-cancel" title="Cancel" onClick={cancelCategoryEdit}>
                            <i className="bi bi-x-lg" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="btn-view me-2" title="Edit" onClick={() => startCategoryEdit(category)}>
                            <i className="bi bi-pencil" />
                          </button>
                          <button className="btn-delete" title="Delete" onClick={() => handleCategoryDelete(category.id)}>
                            <i className="bi bi-trash" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <footer className="dashboard-footer">
        <small>© {new Date().getFullYear()}  <strong>སྟབས་བདེ། </strong> Admin • All rights reserved</small>
      </footer>
    </>
  );
}
